import React, { useEffect, useRef } from "react";

import { useContextScope } from "@/hooks/useContextTree";
import { cn } from "@/components/ui/utils";
import { ListContext, useList, useListContext } from "./List.hooks";
import { ListCheckedItems } from "./ListCheckedItems";
import { ListCreateNewFooter } from "./ListCreateNewFooter";
import { ListEditOptionsButton } from "./ListEditOptionsButton";
import { ListItem } from "./ListItem";
import { ListItemDivider } from "./ListItemDivider";
import { ListItemGroup } from "./ListItemGroup";
import { ListItems } from "./ListItems";
import { ListLoadingState } from "./ListLoadingState";
import { ListMultiSelectFooter } from "./ListMultiSelectFooter";
import { ListSearchInput } from "./ListSearchInput";

export function List<T>({
  children,
  data,
  getRowKey = (item: T) => JSON.stringify(item),
  getIsChecked = () => false,
  getShouldSkipRow = () => false,
  filterOptions,
  isMultiselect = false,
  isCheckboxesEnabled = false,
  isNullable = true,
  maxCheckedRows,
  isLoading = false,
  onItemClick,
  onSelectMultiSelectOption,
  onSubmitCheckedRows,
  onNewRow,
  newRowNeedsName = false,
  onActiveIndexChange,
  size = "sm",
  className,
  autofocus = false,
  overscanCount = 1,
  fillHeight = true,
}: {
  children?: React.ReactNode;
  data: T[];
  getRowKey?: (item: T) => string | number;
  getIsChecked?: (item: T, index?: number) => boolean;
  getShouldSkipRow?: (item: T) => boolean;
  filterOptions?: (options: T[], filter: string) => T[];
  isMultiselect?: boolean;
  isCheckboxesEnabled?: boolean;
  isNullable?: boolean;
  maxCheckedRows?: number;
  isLoading?: boolean;
  onItemClick?: (item: T) => void;
  onSelectMultiSelectOption?: (item: T) => void;
  onSubmitCheckedRows?: (
    checkedRows: T[],
    uncheckedRows: T[],
    selectedRow: T,
  ) => void;
  onNewRow?: (filter: string) => void | Promise<unknown>;
  /** What it makes is named by what is typed: with nothing typed, no way to make one. */
  newRowNeedsName?: boolean;
  onActiveIndexChange?: (data: T, onClick: () => void) => void;
  size?: "sm" | "md" | "lg";
  className?: string;
  autofocus?: boolean;
  /** Number of items to render beyond the visible viewport. Higher values prevent progressive reveal. */
  overscanCount?: number;
  /** When false, size to content instead of growing to fill the parent flex area. */
  fillHeight?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { Scope } = useContextScope();

  const listValue = useList({
    data,
    getRowKey,
    getIsChecked,
    getShouldSkipRow,
    filterOptions,
    isMultiselect,
    isCheckboxesEnabled,
    isNullable,
    maxCheckedRows,
    isLoading,
    onItemClick,
    onSelectMultiSelectOption,
    onSubmitCheckedRows,
    onNewRow,
    newRowNeedsName,
    onActiveIndexChange,
    size,
    autofocus,
    containerRef,
    overscanCount,
  });

  // Separate SearchInput and MultiSelectFooter from scrollable children.
  // Recognize SearchInput via identity OR the HMR-stable marker / displayName
  // so a hot-reloaded ``List.SearchInput`` child still matches.
  const isSearchInputChild = (child: React.ReactNode): boolean => {
    if (!React.isValidElement(child)) return false;
    const type = child.type as {
      __isListSearchInput?: boolean;
      displayName?: string;
    };
    return (
      child.type === ListSearchInput ||
      type.__isListSearchInput === true ||
      type.displayName === "List.SearchInput"
    );
  };
  const childArray = React.Children.toArray(children);
  const searchInputChild = childArray.find(isSearchInputChild);
  // At its foot, under what scrolls: the way to make one, then what submits.
  const footerTypes = [
    ListCreateNewFooter,
    ListMultiSelectFooter,
    ListEditOptionsButton,
  ];
  const footerChildren = childArray.filter(
    (child) =>
      React.isValidElement(child) && footerTypes.includes(child.type as any),
  );
  const scrollableChildren = childArray.filter(
    (child) =>
      React.isValidElement(child) &&
      !isSearchInputChild(child) &&
      !footerTypes.includes(child.type as any),
  );

  const searchPlaceholder =
    searchInputChild &&
    React.isValidElement(searchInputChild) &&
    typeof (searchInputChild.props as { placeholder?: unknown }).placeholder ===
      "string"
      ? (searchInputChild.props as { placeholder?: string }).placeholder
      : undefined;

  // Focus the container when autofocus is true and there's no search input
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autofocus && !searchInputChild && rootRef.current) {
      // Use requestAnimationFrame to ensure the component is fully rendered
      requestAnimationFrame(() => {
        rootRef.current?.focus();
      });
    }
  }, [autofocus, searchInputChild]);

  return (
    <div className={cn("flex flex-col", className)}>
      <Scope
        ref={rootRef}
        tabIndex={0}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
          flex: fillHeight ? 1 : "0 1 auto",
          outline: "none",
        }}
      >
        <ListContext.Provider value={listValue}>
          {searchInputChild != null ? (
            <ListSearchInput placeholder={searchPlaceholder} />
          ) : null}

          <div
            ref={containerRef}
            onMouseMove={listValue.enableMouse}
            className={cn(
              fillHeight ? "flex-1" : "flex-[0_1_auto]",
              "min-h-0 overflow-x-hidden overflow-y-auto relative [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
            )}
          >
            {scrollableChildren}
          </div>
          {footerChildren}
        </ListContext.Provider>
      </Scope>
    </div>
  );
}

// Attach sub-components as properties
List.SearchInput = ListSearchInput;
List.Items = ListItems;
List.Item = ListItem;
List.ItemGroup = ListItemGroup;
List.ItemDivider = ListItemDivider;
List.CheckedItems = ListCheckedItems;
List.MultiSelectFooter = ListMultiSelectFooter;
List.CreateNewFooter = ListCreateNewFooter;
List.EditOptionsButton = ListEditOptionsButton;
List.LoadingState = ListLoadingState;

// Export context hook for custom components
List.useListContext = useListContext;

// Export individual components for direct import if needed
export {
  ListCheckedItems,
  ListCreateNewFooter,
  ListEditOptionsButton,
  ListItem,
  ListItemDivider,
  ListItemGroup,
  ListItems,
  ListLoadingState,
  ListMultiSelectFooter,
  ListSearchInput,
  useListContext,
};
